import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { Store, ExternalLink, Copy, Trash2, Zap, BadgeCheck, Search } from 'lucide-react';
import {
  AffiliateProduct,
  UserStoreItem,
  EarningsStats,
  getAffiliateCatalog,
  getMyShop,
  leakProduct,
  removeFromShop,
  getEarningsStats,
  AffiliatePartner,
  applyAffiliatePartner,
  getAffiliatePartnerStatus,
} from '../../api/affiliate/affiliate.api';
import { useConsentGate } from '../../hooks/use-consent-gate';
import AffiliateDashboard from '../../components/affiliate/affiliate-dashboard';

const money = (v: number) => (v || 0).toLocaleString('vi-VN') + 'đ';

const ShopPage: React.FC = () => {
  const affiliateGate = useConsentGate('affiliate');
  const [partner, setPartner] = useState<AffiliatePartner | null>(null);
  const [applying, setApplying] = useState(false);
  const [tab, setTab] = useState<'catalog' | 'mine' | 'dashboard'>('catalog');
  const [search, setSearch] = useState('');
  const [catalog, setCatalog] = useState<AffiliateProduct[]>([]);
  const [mine, setMine] = useState<UserStoreItem[]>([]);
  const [stats, setStats] = useState<EarningsStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const loadCatalog = async (q?: string) => {
    try {
      setLoading(true);
      setCatalog(await getAffiliateCatalog(q));
    } finally {
      setLoading(false);
    }
  };
  const loadMine = async () => {
    try {
      setLoading(true);
      setMine(await getMyShop());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    tab === 'catalog' ? loadCatalog(search) : loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    getAffiliatePartnerStatus().then(setPartner).catch(() => undefined);
    getEarningsStats(30).then(setStats).catch(() => undefined);
  }, []);

  const isApproved = partner?.status === 'Approved';

  const apply = async () => {
    try {
      setApplying(true);
      const p = await applyAffiliatePartner();
      setPartner(p);
      toast.success('Đã gửi đăng ký cộng tác viên. Chờ duyệt để bắt đầu lấy sản phẩm về bán.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Đăng ký thất bại.');
    } finally {
      setApplying(false);
    }
  };

  const handleLeak = (p: AffiliateProduct) => {
    if (!isApproved) {
      toast('Bạn cần đăng ký làm cộng tác viên và được duyệt trước.', { icon: '🧑‍💼' });
      return;
    }
    affiliateGate.run(() => doLeak(p));
  };

  const doLeak = async (p: AffiliateProduct) => {
    try {
      setBusy(p.id);
      await leakProduct(p.id);
      toast.success('Đã Leak về Shop của bạn. Gửi link đi để nhận hoa hồng.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Leak thất bại.');
    } finally {
      setBusy(null);
    }
  };

  const doRemove = async (s: UserStoreItem) => {
    try {
      setBusy(s.storeProductId);
      await removeFromShop(s.storeProductId);
      setMine((prev) => prev.filter((x) => x.storeProductId !== s.storeProductId));
    } finally {
      setBusy(null);
    }
  };

  const copyLink = (link: string) => {
    navigator.clipboard?.writeText(link);
    toast.success('Đã copy link tiếp thị.');
  };

  const TABS: { key: typeof tab; label: string }[] = [
    { key: 'catalog', label: 'Kho sản phẩm' },
    { key: 'mine', label: `Sản phẩm của tôi${mine.length ? ` (${mine.length})` : ''}` },
    { key: 'dashboard', label: 'Thu nhập & đối soát' },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      <Helmet>
        <title>Tiếp thị liên kết — Cab.vn</title>
      </Helmet>

      {/* Header + trust stats */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Store className="text-[#0071A6]" />
            <h1 className="text-2xl font-bold ui-text">Tiếp thị liên kết</h1>
            {isApproved && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
                <BadgeCheck size={13} /> Cộng tác viên đã kích hoạt
              </span>
            )}
          </div>
          <p className="text-sm ui-muted mt-1 max-w-2xl">
            Chọn sản phẩm rồi <b>Leak về trang của bạn</b> và gửi link đi. Có người mua qua link đó là bạn được <b>hoa hồng</b>, trả bằng Ca và rút được.
          </p>
        </div>
        {isApproved && (
          <div className="ui-card border ui-divider rounded-2xl p-4 grid grid-cols-2 gap-4 text-center lg:min-w-[240px]">
            <div>
              <div className="text-xs ui-muted mb-0.5">Click (30 ngày)</div>
              <div className="text-lg font-bold ui-text">{(stats?.totalClicks ?? 0).toLocaleString('vi-VN')}</div>
            </div>
            <div>
              <div className="text-xs ui-muted mb-0.5">Tỉ lệ chuyển đổi</div>
              <div className="text-lg font-bold text-[#0071A6]">{stats?.conversionRate ?? 0}%</div>
            </div>
          </div>
        )}
      </div>

      {/* Cổng đăng ký cộng tác viên */}
      {partner && partner.status !== 'Approved' && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-amber-800 dark:text-amber-200">
            {partner.status === 'Pending' ? (
              <>🕒 Đăng ký cộng tác viên của bạn <b>đang chờ duyệt</b>. Được duyệt xong bạn mới lấy sản phẩm về bán được.</>
            ) : partner.status === 'Rejected' ? (
              <>❌ Đăng ký bị từ chối{partner.rejectReason ? `: ${partner.rejectReason}` : ''}. Bạn có thể đăng ký lại.</>
            ) : (
              <>🧑‍💼 Muốn kiếm hoa hồng? <b>Đăng ký làm cộng tác viên</b> để lấy sản phẩm về bán.</>
            )}
          </div>
          {partner.status !== 'Pending' && (
            <button
              onClick={apply}
              disabled={applying}
              className="rounded-md bg-amber-600 text-white text-sm px-4 py-1.5 hover:brightness-110 disabled:opacity-50 shrink-0"
            >
              {applying ? 'Đang gửi…' : 'Đăng ký cộng tác viên'}
            </button>
          )}
        </div>
      )}

      {/* Card chính: tabs + nội dung */}
      <div className="ui-card border ui-divider rounded-2xl p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-5 border-b ui-divider pb-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-sm rounded-xl px-5 py-2.5 font-semibold transition ${
                tab === t.key
                  ? 'bg-[#0071A6] text-white shadow-md shadow-[#0071A6]/20'
                  : 'ui-muted hover:bg-black/5 dark:hover:bg-white/5'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'catalog' && (
          <div className="flex gap-2 mb-5">
            <div className="flex-1 flex items-center gap-2 rounded-xl ui-divider border px-3">
              <Search size={16} className="ui-muted shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadCatalog(search)}
                placeholder="Tìm sản phẩm…"
                className="flex-1 bg-transparent py-2.5 text-sm outline-none ui-text"
              />
            </div>
            <button onClick={() => loadCatalog(search)} className="rounded-xl bg-[#0071A6] text-white px-5 text-sm font-semibold">
              Tìm
            </button>
          </div>
        )}

        {tab === 'dashboard' ? (
          <AffiliateDashboard />
        ) : loading ? (
          <p className="ui-muted text-sm py-8 text-center">Đang tải…</p>
        ) : tab === 'catalog' ? (
          catalog.length === 0 ? (
            <p className="ui-muted text-sm py-8 text-center">
              Chưa có sản phẩm nào. Vui lòng quay lại sau nhé.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {catalog.map((p) => (
                <div
                  key={p.id}
                  className="rounded-2xl ui-divider border p-4 flex flex-col hover:shadow-md transition bg-black/[0.015] dark:bg-white/[0.02]"
                >
                  {p.thumbnailUrl ? (
                    <img src={p.thumbnailUrl} alt="" className="w-full h-32 rounded-xl object-cover mb-3" />
                  ) : (
                    <div className="w-full h-32 rounded-xl bg-slate-100 dark:bg-slate-700 mb-3 grid place-items-center">
                      <Store className="ui-muted" />
                    </div>
                  )}
                  <span className="text-[11px] font-semibold text-[#0071A6] bg-[#0071A6]/10 rounded-md px-2 py-0.5 self-start mb-2">
                    {p.network === 'Internal' ? '⭐ Cab' : p.network}
                  </span>
                  <span className="font-semibold ui-text line-clamp-2 mb-1">{p.title}</span>
                  <span className="text-sm ui-text font-bold">{money(p.price)}</span>
                  <div className="flex items-center justify-between mt-3 mb-3 text-xs font-semibold rounded-lg ui-divider border px-2.5 py-2">
                    <span className="ui-muted">Hoa hồng</span>
                    <span className="text-emerald-600 dark:text-emerald-400">{(p.commissionRate * 100).toFixed(0)}%</span>
                  </div>
                  <button
                    onClick={() => handleLeak(p)}
                    disabled={busy === p.id}
                    className="mt-auto inline-flex items-center justify-center gap-1 rounded-xl bg-[#0071A6] text-white text-sm font-semibold py-2.5 disabled:opacity-50 hover:brightness-110"
                  >
                    <Zap size={14} /> Lấy link về Shop
                  </button>
                </div>
              ))}
            </div>
          )
        ) : mine.length === 0 ? (
          <p className="ui-muted text-sm py-8 text-center">Bạn chưa Leak sản phẩm nào.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="ui-muted text-left text-xs uppercase tracking-wider border-b ui-divider">
                  <th className="py-2.5 px-2">Sản phẩm</th>
                  <th className="py-2.5 px-2">Link chia sẻ</th>
                  <th className="py-2.5 px-2 text-center">Click</th>
                  <th className="py-2.5 px-2 text-center">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {mine.map((s) => (
                  <tr key={s.storeProductId} className="border-b ui-divider">
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-3 min-w-[180px]">
                        {s.thumbnailUrl ? (
                          <img src={s.thumbnailUrl} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="w-11 h-11 rounded-lg bg-slate-100 dark:bg-slate-700 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="font-medium ui-text truncate max-w-[220px]">{s.title}</div>
                          <div className="text-xs ui-muted">{money(s.price)} · HH {(s.commissionRate * 100).toFixed(0)}%</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      <span className="font-mono text-xs text-[#0071A6] truncate inline-block max-w-[200px] align-middle">
                        {s.trackedLink || s.deeplink}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center font-semibold ui-text">{(s.clicks ?? 0).toLocaleString('vi-VN')}</td>
                    <td className="py-3 px-2">
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => copyLink(s.trackedLink || s.deeplink)} title="Copy link chia sẻ" className="rounded-md ui-divider border p-2 hover:bg-black/5 dark:hover:bg-white/5">
                          <Copy size={15} className="ui-text" />
                        </button>
                        <a href={s.trackedLink || s.deeplink} target="_blank" rel="noopener noreferrer" title="Mở link" className="rounded-md ui-divider border p-2 hover:bg-black/5 dark:hover:bg-white/5">
                          <ExternalLink size={15} className="ui-text" />
                        </a>
                        <button onClick={() => doRemove(s)} disabled={busy === s.storeProductId} title="Gỡ" className="rounded-md ui-divider border p-2 hover:bg-rose-50 dark:hover:bg-rose-900/20">
                          <Trash2 size={15} className="text-rose-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {affiliateGate.modal}
    </div>
  );
};

export default ShopPage;
